import { faChevronCircleLeft, faChevronCircleRight, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment, ReactNode, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import Editor, { ShaderSource } from './Editor';
import { exampleFractals } from './exampleFractals';
import { helpMarkdown } from './help';
import { loadFractals, saveFractals, StoredFractal } from './storage';

// A saved fractal entry
interface FractalEntryProps {
  data: StoredFractal;
  onSelect: () => void;
  allowEdit: boolean;
  onDelete: () => void;
  onRename: (name: string) => void;
  showMoveLeft: boolean;
  onMoveLeft: () => void;
  showMoveRight: boolean;
  onMoveRight: () => void;
}

export function FractalEntry(props: FractalEntryProps) {
  return (
    <div 
      className="fractalEntry"
      onClick={props.allowEdit ? () => {} : props.onSelect}
    >
      <div 
        className="fractalEntryImg"
        onClick={props.onSelect}
      >
        <img src={props.data.image} alt={props.data.name}></img>
      </div>
      <div className="fractalEntryHeader flex">
        {props.showMoveLeft &&
          <div 
            className="fractalEntryBtn"
            onClick={props.onMoveLeft}
          >
            <FontAwesomeIcon icon={faChevronCircleLeft} />
          </div>
        }
        {props.allowEdit &&
          <input
            type="text" 
            className="fractalEntryName"
            value={props.data.name}
            onChange={(event) => props.onRename(event.target.value)}
          />
        }
        {!props.allowEdit &&
          <div className="fractalEntryName">{props.data.name}</div>
        }
        {props.showMoveRight &&
          <div 
            className="fractalEntryBtn"
            onClick={props.onMoveRight}
          >
            <FontAwesomeIcon icon={faChevronCircleRight} />
          </div>
        }
        {props.allowEdit &&
          <div 
            className="fractalEntryBtn"
            onClick={(event) => {
              if(window.confirm(`Are you sure you want to delete "${props.data.name}"?`)) {
                props.onDelete();
              }
              event.stopPropagation();
            }}
          >
            <FontAwesomeIcon icon={faTrash}/>
          </div>
        }
      </div>
    </div>
  );
}

// The main application menu, which lets the user select from saved fractals or create a new one
export interface MainMenuProps {
  sources: ShaderSource[];
}

export default function MainMenu(props: MainMenuProps) {
  // if the editor is open, the fractal props it is displaying. null otherwise
  const [fractalProps, setFractalProps] = useState<any | undefined>(undefined);
  // user saved fractals
  const [userFractals, setUserFractals] = useState(loadFractals());
  // an application wide popup window content
  const [popup, setPopup] = useState<ReactNode | null>(null);

  // check for base64 encoded fractal props in url
  useEffect(() => {
    if(window.location.hash) {
      const b64Data = window.location.hash.replace("#", "");
      setFractalProps(JSON.parse(atob(b64Data)));
    }
  }, []);

  const popupWindow = (
    <div className="popupWindow">
      <button 
        className="popupClose"
        onClick={() => { setPopup(null); }}
      >X</button>
      {popup}
    </div>
  );

  if(fractalProps !== undefined) {
    return (
      <Fragment>
        {popup !== null && popupWindow}
        <Editor 
          sources={props.sources} 
          fractalProps={fractalProps}
          onClose={() => {
            setFractalProps(undefined);
            setUserFractals(loadFractals());
            setPopup(null);
          }}
          onPopupWindow={setPopup}
        />
      </Fragment>
    );
  } else {
    return (
      <div>
        {popup !== null && popupWindow}
        <div className="header flex">
          <div className="headerTitle">Fractal Explorer</div>
          <div className="headerButton">
            <button
              onClick={() => setPopup(
                <div className="shaderDescription">
                  <ReactMarkdown rehypePlugins={[rehypeRaw]}>{helpMarkdown}</ReactMarkdown>
                </div>
              )}
            >
              Help
            </button>
          </div>
        </div>
        <div className="menuBody">
          <div 
            className="menuNew"
            onClick={() => {setFractalProps(null);}}
          >
            New Empty Fractal
          </div>
          <div className="menuLabel">User Saved Fractals:</div>
          <div className="fractalGrid">
            {userFractals.map((fractal, index) =>
              <FractalEntry
                key={index}
                data={fractal} 
                allowEdit={true}
                showMoveLeft={index > 0}
                showMoveRight={index < userFractals.length - 1}
                onDelete={() => {
                  const newFractals = [...userFractals.slice(0, index), ...userFractals.slice(index + 1)];
                  setUserFractals(newFractals);
                  saveFractals(newFractals);
                }}
                onRename={(name: string) => {
                  const newFractals = [
                    ...userFractals.slice(0, index), 
                    { ...userFractals[index], name: name }, 
                    ...userFractals.slice(index + 1)
                  ];
                  setUserFractals(newFractals);
                  saveFractals(newFractals);
                }}
                onMoveLeft={() => {
                  const newFractals = [...userFractals.slice(0, index - 1), userFractals[index], userFractals[index - 1], ...userFractals.slice(index + 1)];
                  setUserFractals(newFractals);
                  saveFractals(newFractals);
                }}
                onMoveRight={() => {
                  const newFractals = [...userFractals.slice(0, index), userFractals[index + 1], userFractals[index], ...userFractals.slice(index + 2)];
                  setUserFractals(newFractals);
                  saveFractals(newFractals);
                }}
                onSelect={() => {
                  setFractalProps(fractal.data);
                }}
              />
            )}
          </div>

          <div className="menuLabel">Example Fractals:</div>
          <div className="fractalGrid">
            {exampleFractals.map((fractal, index) => 
              <FractalEntry
                key={index}
                data={fractal}
                allowEdit={false}
                showMoveLeft={false}
                showMoveRight={false}
                onMoveLeft={() => {}}
                onMoveRight={() => {}}
                onDelete={() => {}}
                onRename={() => {}}
                onSelect={() => {
                  setFractalProps(fractal.data)
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}